import {
    DocumentTaxonomy,
    DocumentTaxonomyMainCategories,
    DOCUMENT_TAXONOMY_MAIN_TO_SUB,
} from "./documentCategories";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_LABELS,
} from "./types/reports/report-type";

export const DOCUMENT_TAXONOMY_AI_DESCRIPTIONS: Record<
    DocumentTaxonomy,
    { label: string; description: string }
> = {
    // Main categories
    [DocumentTaxonomy.tax]: {
        label: "Tax",
        description:
            "Generally tax-related documents like taxpayer forms, mileage/home-office documentation, or estimated taxes",
    },
    [DocumentTaxonomy.receipts]: {
        label: "Receipts",
        description:
            "Receipts for purchases, meals, services, and business expenses",
    },
    [DocumentTaxonomy.account_statements]: {
        label: "Account Statements",
        description:
            "Bank statements, credit card statements, Payment Processor statements, and Investment Account statements",
    },
    [DocumentTaxonomy.loan_statements]: {
        label: "Loan Statements",
        description:
            "Auto loans, working capital loans, SBA loans, mortgage, line of credit, merchant cash advance - statements, agreements, and payment schedules.",
    },
    [DocumentTaxonomy.lease_statements]: {
        label: "Lease Statements",
        description:
            "Lease Statements like legal agreements for leased equipment, vehicles, or property.",
    },
    [DocumentTaxonomy.formation_documents]: {
        label: "Formation Documents",
        description:
            "Foundational documents like Partnership Agreement, Articles of Incorporation",
    },
    [DocumentTaxonomy.legal_documents]: {
        label: "Legal Documents",
        description:
            "Legal-related documents are Operating Agreement, Contract",
    },
    [DocumentTaxonomy.accounts_receivable_payable]: {
        label: "Accounts Receivable & Payable",
        description:
            "AR/AP-related documents are Invoices, Customer Information, Bills, and Vendor Information records (unless date of issue and date due are the same, then it's not an invoice, but a receipt)",
    },
    [DocumentTaxonomy.financial_statements]: {
        label: "Financial Statement",
        description:
            "Financial-statement-related documents are Balance Sheet, Profit & Loss, Cash Flow Statement, General Ledger, Trial Balance, Chart of Accounts, Backup",
    },
    [DocumentTaxonomy.payroll]: {
        label: "Payroll",
        description:
            "Payroll-related documents like Payroll journal reports, employee payment records, Forms W-3, W-2, 940, 941",
    },
    [DocumentTaxonomy.checks]: {
        label: "Checks",
        description:
            "Copies or records of physical or electronic checks issued or received.",
    },
    [DocumentTaxonomy.assets]: {
        label: "Assets",
        description:
            "Asset-related documents like purchase and sale agreements, asset depreciation and amortization schedules",
    },
    [DocumentTaxonomy.transaction_records]: {
        label: "Transactions",
        description:
            "Non-PDF list of transactions, that isn't an official account statement from a financial institution.",
    },
    [DocumentTaxonomy.uncategorized]: {
        label: "Uncategorized",
        description:
            "Internal filter-only category for documents without an assigned document type. Not used for classification.",
    },
    [DocumentTaxonomy.other]: {
        label: "Other",
        description:
            "Documents that don't fit into the rest. e.g. Coupon, Correspondence",
    },

    // Subcategories
    [DocumentTaxonomy.tax_return]: {
        label: "Tax Return",
        description:
            "Annual tax filings with federal, state, and local authorities. Forms 1040, 1065, 1120, 1120-S, etc.",
    },
    [DocumentTaxonomy.form_1099]: {
        label: "Form 1099",
        description:
            "Annual form used to report various types of non-employee income to the IRS and recipients.",
    },
    [DocumentTaxonomy.form_w9]: {
        label: "Form W-9",
        description:
            "Forms providing taxpayer demographic info (name, entity type, address, SSN or EIN), typically used to collect info for issuing 1099s.",
    },
    [DocumentTaxonomy.mileage]: {
        label: "Mileage",
        description:
            "Documents related to business miles driven for the purpose of calculating deductible vehicle expenses.",
    },
    [DocumentTaxonomy.home_office]: {
        label: "Home Office",
        description:
            "Documentation supporting the use of a home office space for business, including square footage and usage.",
    },
    [DocumentTaxonomy.estimated_taxes]: {
        label: "Estimated Taxes",
        description:
            "Documents related to quarterly tax payments made to the IRS or state tax authorities by self-employed individuals or businesses.",
    },
    [DocumentTaxonomy.bank_statement]: {
        label: "Bank Statement",
        description:
            "Monthly summary of all transactions and balances in a checking or savings account.",
    },
    [DocumentTaxonomy.credit_card_statement]: {
        label: "Credit Card Statement",
        description:
            "Detailed summary of charges, payments, and balances on a credit card account.",
    },
    [DocumentTaxonomy.payment_processor_statement]: {
        label: "Payment Processor Statement",
        description:
            "Summary of transactions processed through platforms like Stripe, PayPal, or Square.",
    },
    [DocumentTaxonomy.investment_account_statement]: {
        label: "Investment Account Statement",
        description:
            "Periodic reports detailing holdings, trades, dividends, and interest in investment accounts.",
    },
    [DocumentTaxonomy.vehicle_loan_statement]: {
        label: "Vehicle Loan Statement",
        description:
            "Statement showing balance, interest, and payments related to an auto loan.",
    },
    [DocumentTaxonomy.mortgage_loan_statement]: {
        label: "Mortgage Loan Statement",
        description:
            "Summary of mortgage activity including payments, interest, insurance, taxes, and principal balance.",
    },
    [DocumentTaxonomy.working_capital_loan_statement]: {
        label: "Working Capital Loan Statement",
        description:
            "Documentation of a short-term loan intended to finance daily operations.",
    },
    [DocumentTaxonomy.line_of_credit_statement]: {
        label: "Line of Credit Statement",
        description:
            'Statement reflecting activity on a revolving credit line, including draws and repayments (often called "LOC").',
    },
    [DocumentTaxonomy.merchant_cash_advance_statement]: {
        label: "Merchant Cash Advance Statement",
        description:
            "Summary of an advance based on future receivables, repaid via a percentage of sales.",
    },
    [DocumentTaxonomy.loan_agreement]: {
        label: "Loan Agreement",
        description:
            "Legal contract outlining terms and conditions of a loan between borrower and lender.",
    },
    [DocumentTaxonomy.loan_payment_schedule]: {
        label: "Loan Payment Schedule",
        description:
            "Amortization schedule showing principal and interest due over the life of the loan.",
    },
    [DocumentTaxonomy.partnership_agreement]: {
        label: "Partnership Agreement",
        description:
            "Foundational document outlining ownership, responsibilities, and profit sharing for a partnership.",
    },
    [DocumentTaxonomy.articles_of_incorporation]: {
        label: "Articles of Incorporation",
        description:
            "Legal document filed to formally establish a corporation in a specific state.",
    },
    [DocumentTaxonomy.operating_agreement]: {
        label: "Operating Agreement",
        description:
            "Agreement among entity members outlining ownership structure and operating procedures.",
    },
    [DocumentTaxonomy.contract]: {
        label: "Contract",
        description:
            "Binding agreement between two or more parties outlining obligations and expectations.",
    },
    [DocumentTaxonomy.invoice]: {
        label: "Invoice",
        description:
            "Bill issued by a business to a customer for goods delivered or services rendered. (unless date of issue and date due are the same, then it's not an invoice, but a receipt)",
    },
    [DocumentTaxonomy.customer_information]: {
        label: "Customer Information",
        description:
            "Contact, billing, and tax information for customers, used for invoicing and collections.",
    },
    [DocumentTaxonomy.bill]: {
        label: "Bill",
        description:
            "Invoice received from a vendor for goods or services purchased on credit.",
    },
    [DocumentTaxonomy.vendor_information]: {
        label: "Vendor Information",
        description:
            "Records containing payment, contact, and tax details of suppliers and service providers.",
    },
    [DocumentTaxonomy.balance_sheet]: {
        label: ACCOUNTING_REPORT_LABELS[AccountingReportType.BALANCE_SHEET],
        description:
            "Snapshot of a company's financial position, listing assets, liabilities, and equity.",
    },
    [DocumentTaxonomy.profit_and_loss]: {
        label: ACCOUNTING_REPORT_LABELS[AccountingReportType.PROFIT_LOSS],
        description:
            "Report summarizing revenues, costs, and expenses over a specific time period.",
    },
    [DocumentTaxonomy.cash_flow_statement]: {
        label: ACCOUNTING_REPORT_LABELS[
            AccountingReportType.CASH_FLOW_STATEMENT
        ],
        description:
            "Report showing cash inflows and outflows from operating, investing, and financing activities.",
    },
    [DocumentTaxonomy.general_ledger]: {
        label: ACCOUNTING_REPORT_LABELS[AccountingReportType.GENERAL_LEDGER],
        description:
            "Complete record of all financial transactions organized by account.",
    },
    [DocumentTaxonomy.trial_balance]: {
        label: ACCOUNTING_REPORT_LABELS[AccountingReportType.TRIAL_BALANCE],
        description:
            "Internal report listing all ledger accounts and their balances to check for accuracy.",
    },
    [DocumentTaxonomy.revenue_schedules]: {
        label: ACCOUNTING_REPORT_LABELS[AccountingReportType.SCHEDULES],
        description:
            "Rollforward of deferred and accrued revenue schedule balances and waterfall of recognized revenue over a period.",
    },
    [DocumentTaxonomy.chart_of_accounts]: {
        label: "Chart of Accounts",
        description:
            "List of all accounts used in a company's general ledger, organized by account type and subtype, often with account codes.",
    },
    [DocumentTaxonomy.bank_reconciliation]: {
        label: "Bank Reconciliation",
        description:
            "Report comparing the bank statement balance to the general ledger balance, listing unreconciled transactions.",
    },
    [DocumentTaxonomy.form_w3]: {
        label: "Form W-3",
        description:
            "Summary form submitted to the SSA summarizing all employee W-2 forms issued for the year.",
    },
    [DocumentTaxonomy.form_w2]: {
        label: "Form W-2",
        description:
            "Statement provided to employees detailing wages and taxes withheld for the year.",
    },
    [DocumentTaxonomy.form_940]: {
        label: "Form 940",
        description:
            "Annual report used to report and pay federal unemployment taxes (FUTA).",
    },
    [DocumentTaxonomy.form_941]: {
        label: "Form 941",
        description:
            "Quarterly tax form used to report income taxes, Social Security, and Medicare withheld from employee paychecks.",
    },
    [DocumentTaxonomy.payroll_journal_report]: {
        label: "Payroll Journal Report",
        description:
            "Report summarizing payroll transactions by employee and pay period.",
    },
    [DocumentTaxonomy.purchase_and_sale_agreement]: {
        label: "Purchase and Sale Agreement",
        description:
            "Contract documenting the transfer of ownership of a significant asset or business.",
    },
    [DocumentTaxonomy.depreciation_schedule]: {
        label: "Depreciation Schedule",
        description:
            "Record showing the cost, useful life, and depreciation expense of tangible fixed assets.",
    },
    [DocumentTaxonomy.amortization_schedule]: {
        label: "Amortization Schedule",
        description:
            "Record showing the cost, useful life, and amortization expense of intangible assets.",
    },
    [DocumentTaxonomy.correspondence]: {
        label: "Correspondence",
        description:
            "Miscellaneous written communications such as letters or notices from vendors, customers, or tax authorities.",
    },
    [DocumentTaxonomy.miscellaneous]: {
        label: "Other",
        description: "Documents that don't fit into any other category.",
    },
    [DocumentTaxonomy.backup]: {
        label: "Backups",
        description:
            "Full company migration backup file such as a JSON output of the Desktop Extractor.",
    },
};

interface TaxonomyLabelsWithDescriptions {
    labels: Array<string | null>;
    descriptions: string[];
}

export function getDocumentTaxonomyCategoryLabels(): TaxonomyLabelsWithDescriptions {
    const mainKeys = Object.keys(
        DOCUMENT_TAXONOMY_MAIN_TO_SUB,
    ) as DocumentTaxonomyMainCategories[];
    return {
        labels: [
            ...mainKeys.map((k) => DOCUMENT_TAXONOMY_AI_DESCRIPTIONS[k].label),
            null,
        ],
        descriptions: [
            ...mainKeys.map(
                (k) => DOCUMENT_TAXONOMY_AI_DESCRIPTIONS[k].description,
            ),
            "",
        ],
    };
}

export function getDocumentTaxonomySubcategoryLabels(): TaxonomyLabelsWithDescriptions {
    const allSubs = Object.values(DOCUMENT_TAXONOMY_MAIN_TO_SUB).flat();
    return {
        labels: [
            ...allSubs.map((s) => DOCUMENT_TAXONOMY_AI_DESCRIPTIONS[s].label),
            null,
        ],
        descriptions: [
            ...allSubs.map(
                (s) => DOCUMENT_TAXONOMY_AI_DESCRIPTIONS[s].description,
            ),
            "",
        ],
    };
}
